/**
 * Camera utility functions for global camera management
 */

export const CAMERA_ASPECT_RATIO_OPTIONS = [
  { value: '16:9', label: '16:9 Widescreen (HD)', ratio: 16 / 9 },
  { value: '4:3', label: '4:3 Standard', ratio: 4 / 3 },
  { value: 'native', label: 'Native (camera default)', ratio: null }
];

export const DEFAULT_CAMERA_ASPECT_RATIO = '16:9';

export const ASPECT_RATIO_STORAGE_KEY = 'selectedCameraAspectRatio';

/**
 * Safely parse user session data from sessionStorage
 * @returns {Object|null} Parsed user data or null if invalid
 */
export const getSafeUserSession = () => {
  try {
    const userSession = sessionStorage.getItem('user');
    if (!userSession) {
      console.warn('No user session found in sessionStorage');
      return null;
    }

    const userData = JSON.parse(userSession);

    if (
      !userData ||
      !userData.signInUserSession ||
      !userData.signInUserSession.idToken
    ) {
      console.warn('Invalid user session data structure');
      return null;
    }

    return userData;
  } catch (error) {
    console.error('Error parsing user session data:', error);
    return null;
  }
};

/**
 * Get the current camera preference from localStorage
 * @returns {string} The selected camera value
 */
export const getSelectedCamera = () => {
  return localStorage.getItem('selectedCamera') || 'user';
};

/**
 * Get the current aspect ratio preference from localStorage
 * @returns {string} Aspect ratio key (16:9, 4:3, native)
 */
export const getSelectedAspectRatio = () => {
  return (
    localStorage.getItem(ASPECT_RATIO_STORAGE_KEY) ||
    DEFAULT_CAMERA_ASPECT_RATIO
  );
};

/**
 * Resolve numeric aspect ratio for a stored key
 * @param {string} aspectRatioKey
 * @returns {number|null}
 */
export const getAspectRatioNumeric = (aspectRatioKey = null) => {
  const key = aspectRatioKey || getSelectedAspectRatio();
  const option = CAMERA_ASPECT_RATIO_OPTIONS.find((item) => item.value === key);
  return option ? option.ratio : 16 / 9;
};

/**
 * Build resolution + aspectRatio constraints for the selected aspect ratio
 * @param {string} aspectRatioKey
 * @returns {Object}
 */
export const getResolutionForAspectRatio = (aspectRatioKey = null) => {
  const key = aspectRatioKey || getSelectedAspectRatio();

  if (key === 'native') {
    return {};
  }

  const ratio = getAspectRatioNumeric(key);

  if (ratio <= 4 / 3 + 0.01) {
    return {
      width: { ideal: 1280 },
      height: { ideal: 960 },
      aspectRatio: { ideal: ratio }
    };
  }

  return {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    aspectRatio: { ideal: ratio }
  };
};

/**
 * Save aspect ratio preference to localStorage
 * @param {string} aspectRatioValue
 */
export const saveAspectRatioPreference = (aspectRatioValue) => {
  localStorage.setItem(ASPECT_RATIO_STORAGE_KEY, aspectRatioValue);
};

/**
 * Preview box dimensions that mirror the selected aspect ratio
 * @param {number} baseWidth
 * @param {string} aspectRatioKey
 * @returns {{ width: number, height: number }}
 */
export const getPreviewContainerDimensions = (
  baseWidth = 320,
  aspectRatioKey = null
) => {
  const key = aspectRatioKey || getSelectedAspectRatio();
  const ratio = getAspectRatioNumeric(key);

  if (!ratio || key === 'native') {
    return { width: baseWidth, height: 240 };
  }

  return {
    width: baseWidth,
    height: Math.round(baseWidth / ratio)
  };
};

/**
 * Explicit zoom factor per aspect-ratio option, applied as a CSS
 * transform: scale() on the video element. The camera frame keeps a constant
 * width/height (object-fit: cover); only this zoom changes with the selection,
 * so every option (including native) looks clearly different.
 */
export const ASPECT_RATIO_ZOOM = {
  '16:9': 1.3,
  '4:3': 1.76,
  native: 1.56
};

/**
 * Get the zoom factor for the selected (or given) aspect ratio.
 * @param {string} aspectRatioKey
 * @returns {number} scale factor
 */
export const getAspectRatioZoom = (aspectRatioKey = null) => {
  const key = aspectRatioKey || getSelectedAspectRatio();
  return ASPECT_RATIO_ZOOM[key] != null ? ASPECT_RATIO_ZOOM[key] : 1;
};

/**
 * Get video constraints based on the selected camera
 * @param {string} selectedCamera - The selected camera value
 * @param {Object} options - Additional options for video constraints
 * @returns {Object} Video constraints object
 */
export const getVideoConstraints = (selectedCamera = null, options = {}) => {
  const camera = selectedCamera || getSelectedCamera();
  const { aspectRatioKey, ...constraintOptions } = options;
  const defaultOptions = {
    ...getResolutionForAspectRatio(aspectRatioKey),
    ...constraintOptions
  };

  if (camera === 'user') {
    return {
      ...defaultOptions,
      facingMode: 'user'
    };
  } else if (camera === 'environment') {
    return {
      ...defaultOptions,
      facingMode: 'environment'
    };
  } else {
    return {
      ...defaultOptions,
      deviceId: { exact: camera }
    };
  }
};

/**
 * Get video constraints for preview (smaller size)
 * @param {string} selectedCamera - The selected camera value
 * @returns {Object} Video constraints object for preview
 */
export const getPreviewVideoConstraints = (
  selectedCamera = null,
  aspectRatioKey = null
) => {
  const key = aspectRatioKey || getSelectedAspectRatio();
  const { width, height } = getPreviewContainerDimensions(320, key);

  return getVideoConstraints(selectedCamera, {
    width,
    height,
    aspectRatioKey: key
  });
};

/**
 * Save camera preference to localStorage
 * @param {string} cameraValue - The camera value to save
 */
export const saveCameraPreference = (cameraValue) => {
  localStorage.setItem('selectedCamera', cameraValue);
};

/**
 * Get camera capabilities including supported resolutions
 * @param {string} deviceId - The camera device ID
 * @returns {Promise<Object>} Camera capabilities
 */
export const getCameraCapabilities = async (deviceId) => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: deviceId } }
    });
    const track = stream.getVideoTracks()[0];
    const capabilities = track.getCapabilities ? track.getCapabilities() : {};

    // Stop the stream after getting capabilities
    track.stop();
    stream.getTracks().forEach((track) => track.stop());

    return capabilities;
  } catch (error) {
    console.error('Error getting camera capabilities:', error);
    return {};
  }
};

/**
 * Detect available cameras
 * @returns {Promise<Array>} Array of available camera options
 */
export const detectAvailableCameras = async () => {
  try {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === 'videoinput'
      );

      const cameraOptions = [];

      // Only add cameras that have actual device IDs and labels
      videoDevices.forEach((device, index) => {
        if (device.deviceId && device.deviceId !== 'default' && device.label) {
          cameraOptions.push({
            value: device.deviceId,
            label: device.label
          });
        } else if (device.deviceId && device.deviceId !== 'default') {
          // If no label but has deviceId, create a generic name
          cameraOptions.push({
            value: device.deviceId,
            label: `Camera ${index + 1}`
          });
        }
      });

      // If no specific cameras found, add facingMode options as fallback
      if (cameraOptions.length === 0) {
        cameraOptions.push(
          { value: 'user', label: 'Front Camera' },
          { value: 'environment', label: 'Back Camera' }
        );
      }

      return cameraOptions;
    }
  } catch (error) {
    console.error('Error detecting cameras:', error);
  }

  // Fallback when no cameras detected
  return [
    { value: 'user', label: 'Front Camera' },
    { value: 'environment', label: 'Back Camera' }
  ];
};

/**
 * Get camera settings for Webcam component
 * @param {Object} additionalProps - Additional props to merge
 * @returns {Object} Props for Webcam component
 */
export const getWebcamProps = (additionalProps = {}) => {
  const selectedCamera = getSelectedCamera();
  const videoConstraints = getVideoConstraints(selectedCamera);

  return {
    audio: false,
    screenshotFormat: 'image/png',
    videoConstraints,
    style: {
      border: '2px solid #2F5597',
      objectFit: 'contain', // Ensure full frame is visible without cropping
      width: '100%',
      height: '100%'
    },
    ...additionalProps
  };
};

/**
 * Get camera settings for preview Webcam component
 * @param {Object} additionalProps - Additional props to merge
 * @returns {Object} Props for preview Webcam component
 */
export const getPreviewWebcamProps = (additionalProps = {}) => {
  const selectedCamera = getSelectedCamera();
  const videoConstraints = getPreviewVideoConstraints(selectedCamera);

  return {
    audio: false,
    screenshotFormat: 'image/png',
    videoConstraints,
    style: {
      width: '100%',
      height: '100%',
      objectFit: 'contain' // Use 'contain' to show full frame
    },
    ...additionalProps
  };
};
